import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Clock, MessageCircle, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/translations";
import { BLOG, ARTICLE_SLUG_TO_KEY, ARTICLE_SLUGS, ARTICLE_IMAGES } from "@/i18n/blog";
import Header from "@/components/landing/Header";
import ContactFooter from "@/components/landing/ContactFooter";
import Seo from "@/components/Seo";
import { Reveal, Overline } from "@/components/landing/primitives";

const SITE = "https://tuningfiles.hu";

const Block = ({ block }) => {
  if (block.type === "h")
    return <h2 className="mt-12 font-display text-2xl font-black text-white md:text-3xl">{block.text}</h2>;
  if (block.type === "ul")
    return (
      <ul className="mt-5 space-y-3">
        {block.items.map((it) => (
          <li key={it} className="flex items-start gap-3 text-white/70">
            <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 bg-brand-green" />
            <span className="text-base leading-relaxed md:text-lg">{it}</span>
          </li>
        ))}
      </ul>
    );
  return <p className="mt-5 text-base leading-relaxed text-white/70 md:text-lg">{block.text}</p>;
};

const BlogArticle = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const key = ARTICLE_SLUG_TO_KEY[slug];

  if (!key) return <Navigate to="/blog" replace />;

  const b = BLOG[lang];
  const a = b.articles[key];
  const img = ARTICLE_IMAGES[key];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        headline: a.title,
        description: a.excerpt,
        image: img,
        datePublished: a.date,
        inLanguage: lang,
        author: { "@type": "Organization", name: "TuningFiles.hu" },
        publisher: { "@type": "Organization", name: "TuningFiles.hu", url: SITE },
        mainEntityOfPage: `${SITE}/blog/${a.slug}`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: b.ui.title, item: `${SITE}/blog` },
          { "@type": "ListItem", position: 2, name: a.title, item: `${SITE}/blog/${a.slug}` },
        ],
      },
    ],
  };

  return (
    <div className="relative min-h-screen bg-[#0A0A0A]" data-testid="blog-article-page">
      <Seo title={a.meta.title} description={a.meta.description} path={`/blog/${a.slug}`} jsonLd={jsonLd} />
      <Header />

      <article className="relative">
        <section className="relative px-5 pt-32 pb-10 md:px-10 md:pt-40">
          <div className="mx-auto max-w-[820px]">
            <nav className="mb-8 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-white/40" data-testid="breadcrumb">
              <Link to="/blog" className="transition-colors hover:text-brand-green">
                {b.ui.title}
              </Link>
              <ChevronRight className="h-3 w-3" />
              <span className="truncate text-white/70">{a.category}</span>
            </nav>

            <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-widest text-brand-green">
              <span>{a.category}</span>
              <span className="h-1 w-1 rounded-full bg-white/30" />
              <span className="flex items-center gap-1 text-white/40">
                <Clock className="h-3 w-3" />
                {a.readTime} {b.ui.readTime}
              </span>
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 font-display text-4xl font-black leading-[1.02] text-white md:text-6xl"
            >
              {a.title}
            </motion.h1>
            <p className="mt-5 text-lg text-white/60">{a.excerpt}</p>
          </div>
        </section>

        <section className="px-5 md:px-10">
          <div className="mx-auto max-w-[1000px]">
            <div className="relative h-[280px] overflow-hidden border border-white/10 md:h-[440px]">
              <img src={img} alt={a.title} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
            </div>
          </div>
        </section>

        <section className="px-5 py-14 md:px-10 md:py-20">
          <div className="mx-auto max-w-[820px]">
            {a.content.map((block, i) => (
              <Block key={i} block={block} />
            ))}

            {/* CTA */}
            <div className="mt-14 flex flex-col items-start gap-5 border-l-4 border-brand-green bg-[#121212] p-8 md:flex-row md:items-center md:justify-between">
              <p className="font-display text-xl font-bold text-white">{b.ui.cta}</p>
              <a
                href={`https://wa.me/${CONTACT.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex flex-shrink-0 items-center gap-2 bg-brand-green px-7 py-4 font-mono text-xs font-medium uppercase tracking-widest text-black transition-transform hover:-translate-y-1"
                data-testid="article-cta-whatsapp"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </a>
            </div>
          </div>
        </section>

        {/* Related */}
        <section className="border-t border-white/10 px-5 py-16 md:px-10 md:py-24">
          <div className="mx-auto max-w-[1000px]">
            <Overline color="red">{b.ui.related}</Overline>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {a.related.map((rk) => {
                const ra = b.articles[rk];
                return (
                  <Link
                    key={rk}
                    to={`/blog/${ARTICLE_SLUGS[rk]}`}
                    className="group flex items-center justify-between border border-white/10 bg-[#121212] p-6 transition-all hover:-translate-y-1 hover:border-brand-green"
                    data-testid={`related-article-${rk}`}
                  >
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-brand-green">
                        {ra.category}
                      </div>
                      <div className="mt-2 font-display text-lg font-bold leading-tight text-white">
                        {ra.title}
                      </div>
                    </div>
                    <ArrowUpRight className="h-6 w-6 flex-shrink-0 text-white/30 transition-colors group-hover:text-brand-green" />
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </article>

      <ContactFooter />
    </div>
  );
};

export default BlogArticle;
